import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const ImageCarousel = ({ 
  images: propImages,
  imageFolder = '/banners/', 
  autoPlay = true, 
  interval = 5000,
  showIndicators = true,
  showArrows = true,
  className = ''
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [images, setImages] = useState(propImages || []);

  useEffect(() => {
    // If images provided as prop, use them directly
    if (propImages && propImages.length > 0) {
      setImages(propImages);
      setIsLoading(false);
      return;
    }
    
    // Otherwise fetch banners.json manifest
    const fetchBanners = async () => {
      try {
        const response = await fetch(`${imageFolder}banners.json`);
        if (response.ok) {
          const data = await response.json();
          setImages(data.images || []);
        }
      } catch (error) {
        console.warn('Failed to load banners.json:', error);
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchBanners();
  }, [propImages, imageFolder]);

  useEffect(() => {
    if (!autoPlay || images.length <= 1) return;

    const timer = setInterval(() => {
      setCurrentIndex((prevIndex) => (prevIndex + 1) % images.length);
    }, interval);

    return () => clearInterval(timer);
  }, [autoPlay, interval, images.length]);

  const goToPrevious = () => {
    setCurrentIndex((prevIndex) => 
      prevIndex === 0 ? images.length - 1 : prevIndex - 1
    );
  };

  const goToNext = () => {
    setCurrentIndex((prevIndex) => (prevIndex + 1) % images.length);
  };

  const goToSlide = (index) => {
    setCurrentIndex(index);
  };

  if (isLoading) {
    return (
      <div className={`w-full h-64 bg-gray-200 animate-pulse rounded-lg ${className}`} />
    );
  }

  // Show placeholder if no images found
  if (images.length === 0) {
    return (
      <div className={`w-full h-64 bg-gradient-to-br from-primary-100 to-secondary-100 rounded-lg flex items-center justify-center ${className}`}>
        <p className="text-primary-600 font-medium">Welcome</p>
      </div>
    );
  }

  return (
    <div className={`relative w-full overflow-hidden rounded-xl shadow-xl border border-primary-200 dark:border-slate-700 ${className}`}>
      <div className="relative w-full h-full bg-primary-50 dark:bg-slate-900 p-8">
        {images.map((image, index) => (
          <div
            key={index}
            className={`absolute inset-0 transition-all duration-1000 ease-in-out ${
              index === currentIndex ? 'opacity-100 scale-100' : 'opacity-0 scale-95'
            }`}
          >
            <img
              src={image.url}
              alt={image.alt}
              className="w-full h-full object-contain"
            />
          </div>
        ))}
      </div>

      {showArrows && images.length > 1 && (
        <>
          <button
            onClick={goToPrevious}
            className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/90 dark:bg-slate-800/90 hover:bg-white dark:hover:bg-slate-800 text-primary-900 dark:text-white p-2 rounded-full shadow-lg backdrop-blur-sm transition-all duration-200 hover:scale-110"
            aria-label="Previous slide"
          >
            <ChevronLeft size={20} />
          </button>
          <button
            onClick={goToNext}
            className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/90 dark:bg-slate-800/90 hover:bg-white dark:hover:bg-slate-800 text-primary-900 dark:text-white p-2 rounded-full shadow-lg backdrop-blur-sm transition-all duration-200 hover:scale-110"
            aria-label="Next slide"
          >
            <ChevronRight size={20} />
          </button>
        </>
      )}

      {showIndicators && images.length > 1 && (
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-1.5">
          {images.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`w-8 h-1 rounded-full transition-all duration-300 ${
                index === currentIndex
                  ? 'bg-white shadow-lg scale-x-125'
                  : 'bg-white/60 hover:bg-white/80'
              }`}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export { ImageCarousel };
